import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import TextField from '../SolvPatternLibrary/TextField';
import { emailRegex } from '../../core/util/email';
import { analyticsTrackEvent } from '../../core/analytics';
import {
  ACCOUNT_INFO_TOUCH_EDIT_EMAIL,
  ACCOUNT_INFO_TOUCH_SAVE_EDITS,
  SETTINGS_UPDATED,
  ACCOUNT_INFO_TOUCH_EDIT_WEB_PUSH,
  ACCOUNT_INFO_TOUCH_CANCEL,
} from '../../core/analytics/events';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import history from '../../core/history';
import { fontSize } from '../../constants/text';
import { colors } from '../../constants/colors';
import Switch from '../SolvPatternLibrary/Switch';
import { canAskToEnablePush } from '../WebPush/util';
import WebPushOptInDialog from '../WebPush/OptInDialog';
import { PRIVACY_DATA_REQUEST_FORM_URL, PUSH_OPT_IN_ORIGIN_SETTINGS } from '../../constants/index';
import { isEmptyObject } from '../../core/util/object';
import { buildDapiAccountObject } from '../../core/dapi/account';
import { isNativeApp } from '../../core/util/device';
import { isClientSide } from '../../core/util/system';
import { NativeFunctions } from '../../core/util/native';
import { isEmptyString } from '../../core/util/string';

const EMAIL_FIELD = 'email';

const Root = styled.div.withConfig({ componentId: 'sc-1hwqqs4-0' })`
  padding: 30px 16px 0 16px;
`;

const Form = styled.form.withConfig({ componentId: 'sc-1hwqqs4-1' })`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 340px;
  margin: 0 auto;
`;

const ButtonsContainer = styled.div.withConfig({ componentId: 'sc-1hwqqs4-2' })`
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
`;

const ActionButton = styled(Button).withConfig({ componentId: 'sc-1hwqqs4-3' })`
  text-transform: uppercase;
  font-size: ${fontSize.mediumSmall};
  display: flex;
  justify-content: flex-start;
  width: 50%;
`;

const SaveActionButton = styled(ActionButton).withConfig({ componentId: 'sc-1hwqqs4-4' })`
  justify-content: flex-end;
`;

const UpdatePrivacyActionButton = styled(ActionButton).withConfig({ componentId: 'sc-1hwqqs4-5' })`
  width: 100%;
`;

const PushPermissionsContainer = styled.div.withConfig({ componentId: 'sc-1hwqqs4-6' })``;
const PushPermissionsTopContainer = styled.div.withConfig({ componentId: 'sc-1hwqqs4-7' })`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
`;
const PushPermissionsTitle = styled.h3.withConfig({ componentId: 'sc-1hwqqs4-8' })`
  font-size: ${fontSize.medium};
`;
const PushPermissionsCopy = styled.p.withConfig({ componentId: 'sc-1hwqqs4-9' })`
  color: ${colors.lightText};
  font-size: ${fontSize.mediumSmall};
`;
const PushPermissionsToggle = styled(Switch).withConfig({ componentId: 'sc-1hwqqs4-10' })``;

type Props = {
  form: formShape;
  accountId: string;
  isUpdatingAccount?: boolean;
  initialValues?: any;
  accountWebPushSubscription?: any;
  updateAccount: (...args: any[]) => any;
  fetchAccountWebPushSubscription: (...args: any[]) => any;
  deleteAccountWebPushSubscription: (...args: any[]) => any;
};

const EditAccountForm = (props: Props) => {
  const [errors, setErrors] = useState({} as any);
  const [showOptInDialog, setShowOptInDialog] = useState(false);
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);

  const {
    form,
    updateAccount,
    accountId,
    accountWebPushSubscription,
    fetchAccountWebPushSubscription,
    initialValues,
    form: { getFieldProps },
    deleteAccountWebPushSubscription,
    isUpdatingAccount,
  } = props;

  const email = initialValues?.email;
  const phone = initialValues?.phone;

  const emailFormValue = form.getFieldValue('email');

  const onFieldChange = (value: any, fieldName: any) => {
    form.setFieldsValue({ [fieldName]: value });

    form.validateFields();
  };

  const onBlur = (e: any, fieldName: any) => {
    const properties = { name: EMAIL_FIELD };
    analyticsTrackEvent(ACCOUNT_INFO_TOUCH_EDIT_EMAIL, properties);

    form.validateFields([fieldName], {}, (errors: any) => {
      if (isEmptyObject(errors)) {
        setErrors({ [fieldName]: false });
      } else {
        setErrors({ [fieldName]: errors[fieldName] });
      }
    });
  };

  const onSubmit = (e: any) => {
    e.preventDefault();

    analyticsTrackEvent(ACCOUNT_INFO_TOUCH_SAVE_EDITS);
    form.validateFields((error: any, values: any) => {
      if (!error) {
        analyticsTrackEvent(SETTINGS_UPDATED, {
          updatedFields: Object.keys(values),
        });
        const accountData = buildDapiAccountObject(values);
        return updateAccount(accountId, accountData);
      }

      return analyticsTrackEvent(SETTINGS_UPDATED, error);
    });
  };

  const openOptInDialog = () => setShowOptInDialog(true);

  const closeOptInDialog = () => setShowOptInDialog(false);

  const handleClickCancel = (e: any) => {
    e.preventDefault();

    analyticsTrackEvent(ACCOUNT_INFO_TOUCH_CANCEL);
    history.goBack();
  };

  const handleClickUpdatePrivacySettings = () => {
    if (isNativeApp()) {
      NativeFunctions.openNativeUrl.call(PRIVACY_DATA_REQUEST_FORM_URL);
    } else if (isClientSide()) {
      window.location.href = PRIVACY_DATA_REQUEST_FORM_URL;
    }
  };

  const didFormFieldsChange = () => !isEmptyString(emailFormValue) && emailFormValue !== email;

  const unsubscribeToWebPush = (accountWebPushSubscription: any) => {
    const subscriptionId = accountWebPushSubscription.id;
    deleteAccountWebPushSubscription(subscriptionId);
  };

  const subscribeToWebPush = () => {
    openOptInDialog();
  };

  const toggleWebPushSubscription = (e: any) => {
    e.preventDefault();

    const isAccountSubscribedToWebPush = !isEmptyObject(accountWebPushSubscription);
    const action = isAccountSubscribedToWebPush ? 'subscribe' : 'unsubscribe';
    analyticsTrackEvent(ACCOUNT_INFO_TOUCH_EDIT_WEB_PUSH, action);

    isAccountSubscribedToWebPush
      ? unsubscribeToWebPush(accountWebPushSubscription)
      : subscribeToWebPush();
  };

  useEffect(() => {
    fetchAccountWebPushSubscription(accountId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsSaveButtonEnabled(didFormFieldsChange());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFormValue]);

  const isAccountSubscribedToWebPush = !isEmptyObject(accountWebPushSubscription);

  return (
    <Root>
      <Form data-testid="form" onSubmit={onSubmit}>
        <TextField
          data-hj-suppress
          disabled
          fullWidth
          id="phone"
          label="Phone Number"
          value={phone}
        />

        <TextField
          data-hj-suppress
          error={Boolean(errors?.email)}
          fullWidth
          id={EMAIL_FIELD}
          label="Email Address"
          onBlur={(e: any) => onBlur(e, EMAIL_FIELD)}
          onChange={({ target: { value } }: any) => onFieldChange(value, EMAIL_FIELD)}
          type="email"
          {...getFieldProps(EMAIL_FIELD, {
            initialValue: email,
            validateTrigger: 'onChange',
            rules: [
              {
                required: true,
                pattern: emailRegex,
                message: 'Invalid email address',
              },
            ],
          })}
        />
        {!isNativeApp() && canAskToEnablePush() && (
          <PushPermissionsContainer>
            <PushPermissionsTopContainer>
              <PushPermissionsTitle>Push Notifications</PushPermissionsTitle>

              <PushPermissionsToggle
                // @ts-expect-error ts-migrate(2769) FIXME: Property 'checked' does not exist on type '(Intrin... Remove this comment to see the full error message
                checked={isAccountSubscribedToWebPush}
                onClick={toggleWebPushSubscription}
              />
            </PushPermissionsTopContainer>

            <PushPermissionsCopy>
              Disabling push notifications will turn off health checklist and appointment reminders
              for this account.
            </PushPermissionsCopy>
          </PushPermissionsContainer>
        )}

        <ButtonsContainer>
          <ActionButton onClick={handleClickCancel} variation={ButtonVariations.link}>
            Cancel
          </ActionButton>
          <SaveActionButton
            data-testid="submit"
            disabled={isUpdatingAccount || !isSaveButtonEnabled}
            type="submit"
            value={isUpdatingAccount ? 'Saving' : 'Save This Info'}
            variation={ButtonVariations.link}
          >
            Save
          </SaveActionButton>
          <UpdatePrivacyActionButton
            onClick={handleClickUpdatePrivacySettings}
            variation={ButtonVariations.link}
          >
            Update Privacy Settings
          </UpdatePrivacyActionButton>
        </ButtonsContainer>
      </Form>

      <WebPushOptInDialog
        accountId={accountId}
        handleAcceptCustomBehavior={closeOptInDialog}
        handleDenyCustomBehavior={closeOptInDialog}
        onClose={closeOptInDialog}
        open={showOptInDialog}
        webPushOptInOrigin={PUSH_OPT_IN_ORIGIN_SETTINGS}
      />
    </Root>
  );
};

export default createForm()(EditAccountForm);
